/**
 * Google Analytics 4 helpers for BEST Galaxy.
 *
 * Measurement ID: G-62E65XSKTE
 *
 * Events (requested by client):
 *   - page_view              website visitors / SPA navigations
 *   - assessment_start       user lands on the assessment form
 *   - assessment_complete    assessment submitted & scored
 *   - sign_up                account email submitted (registration)
 *   - login                  successful login
 *   - purchase               report / lens purchase confirmed (Stripe success)
 *   - begin_checkout         user redirected to Stripe (optional funnel step)
 *
 * Never send PII (email, name) as event parameters.
 */

export const GA_MEASUREMENT_ID = 'G-62E65XSKTE';

function gtag(...args) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  // Mirror the standard gtag stub so calls work before the remote script loads.
  if (typeof window.gtag === 'function') {
    window.gtag(...args);
  } else {
    window.dataLayer.push(args);
  }
}

/** Ensure the GA script + config are present (safe to call multiple times). */
export function initAnalytics() {
  if (typeof window === 'undefined') return;
  if (window.__bestGalaxyGaInitialized) return;
  window.__bestGalaxyGaInitialized = true;

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtagStub() {
      window.dataLayer.push(arguments);
    };
  }

  // If the script tag wasn't injected via index.html, inject it here.
  const existing = document.querySelector(
    `script[src*="googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}"]`,
  );
  if (!existing) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
  }

  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false, // we fire SPA page_views ourselves
    anonymize_ip: true,
  });
}

/** Track a page / SPA route view. */
export function trackPageView(path, title) {
  initAnalytics();
  const pagePath =
    path ||
    (typeof window !== 'undefined'
      ? window.location.pathname + window.location.search
      : '/');
  gtag('event', 'page_view', {
    page_path: pagePath,
    page_title: title || document.title,
    page_location:
      typeof window !== 'undefined' ? window.location.href : undefined,
  });
}

/** Generic custom event. */
export function trackEvent(eventName, params = {}) {
  initAnalytics();
  gtag('event', eventName, params);
}

export function trackAssessmentStart() {
  trackEvent('assessment_start', {
    event_category: 'assessment',
  });
}

export function trackAssessmentComplete(resultId) {
  trackEvent('assessment_complete', {
    event_category: 'assessment',
    ...(resultId ? { assessment_id: resultId } : {}),
  });
}

/** Email / account registration submitted successfully. */
export function trackSignUp(method = 'email') {
  trackEvent('sign_up', {
    method,
  });
}

export function trackLogin(method = 'email') {
  trackEvent('login', {
    method,
  });
}

/** User redirected to Stripe checkout. */
export function trackBeginCheckout(productId) {
  trackEvent('begin_checkout', {
    event_category: 'ecommerce',
    ...(productId ? { items: [{ item_id: productId }] } : {}),
  });
}

/**
 * Report / lens purchase confirmed after Stripe return.
 * Fires once per success page load (caller should guard duplicates).
 */
export function trackPurchase({ productId, assessmentId, value, currency = 'USD' } = {}) {
  trackEvent('purchase', {
    event_category: 'ecommerce',
    currency,
    ...(typeof value === 'number' ? { value } : {}),
    ...(assessmentId ? { transaction_id: assessmentId } : {}),
    ...(productId
      ? {
          items: [
            {
              item_id: productId,
              item_name: productId,
            },
          ],
        }
      : {}),
  });
}
